import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiArrowUpRight } from 'react-icons/fi';
import { customerTestimonials } from '../Data/websiteUpdates';
import voicesMedia from './voicesMedia.json';
import './customer-voices.css';

const testimonials = [0, 2, 1, 3, 4, 5].map(index => customerTestimonials[index]);
const storyPaths = {
  '24 Hour Home Care': '/customer-stories/24-hour-home-care-workforce-modernization',
  Trivago: '/customer-stories/trivago-support-portal',
  Cambri: '/customer-stories/making-arr-easier-to-explain',
  PillTime: '/customer-stories/instant-alerts-connected-care',
  'World YMCA': '/customer-stories/powering-vision-2030',
};
const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));
const smooth = (start, end, value) => {
  const t = clamp((value - start) / (end - start));
  return t * t * (3 - 2 * t);
};
const lastIndex = testimonials.length - 1;

export default function CustomerVoices({ reducedMotion }) {
  return <CustomerVoicesJourney reducedMotion={reducedMotion} />;
}

export function CustomerVoicesJourney({ reducedMotion }) {
  const id = useId();
  const section = useRef(null);
  const stage = useRef(null);
  const aperture = useRef(null);
  const cards = useRef([]);
  const dots = useRef([]);
  const current = useRef(0);
  const pendingAnchor = useRef(null);
  const selectionAnimation = useRef(null);
  const settledPosition = useRef(null);
  const navigationRevision = useRef(0);
  const lastVisible = useRef(false);
  const previousMode = useRef(null);
  const hadDepth = useRef(false);
  const [systemReduced, setSystemReduced] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false);
  const [fits, setFits] = useState(false);
  const [readAll, setReadAll] = useState(false);
  const [position, setPosition] = useState({ timeline: 0, progress: 0, active: 0, arrival: 1 });
  const [announcement, setAnnouncement] = useState('');
  const calm = reducedMotion ?? systemReduced;
  const flow = calm || readAll || !fits;

  useEffect(() => () => {
    selectionAnimation.current?.cancel();
    selectionAnimation.current = null;
  }, [calm, flow]);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) return undefined;
    const change = () => setSystemReduced(query.matches);
    change();
    query.addEventListener?.('change', change);
    return () => query.removeEventListener?.('change', change);
  }, []);

  useLayoutEffect(() => {
    const root = section.current;
    let frame = 0;
    let disposed = false;
    function measure() {
      frame = 0;
      if (disposed) return;
      if (calm || window.innerHeight < 560) {
        setFits(false);
        return;
      }
      // Probe the actual depth layout synchronously, then restore it before paint.
      // All six full quotes must fit; never truncate or reduce text to force depth.
      root.setAttribute('data-cv-measuring', '');
      let allFit = false;
      try {
        allFit = aperture.current.clientHeight >= 300 && cards.current.every(card => {
          const css = getComputedStyle(card);
          const children = Array.from(card.children).filter(child => !child.classList.contains('cv-edge'));
          const contentHeight = children.reduce((height, child) => height + child.offsetHeight, 0);
          const padding = (parseFloat(css.paddingTop) || 0) + (parseFloat(css.paddingBottom) || 0);
          const gaps = (children.length - 1) * (parseFloat(css.rowGap) || 0);
          return contentHeight + padding + gaps <= card.clientHeight - 3 &&
            card.scrollHeight <= card.clientHeight + 2 && card.scrollWidth <= card.clientWidth + 2;
        });
      } finally {
        root.removeAttribute('data-cv-measuring');
      }
      setFits(allFit);
    }
    function schedule() {
      if (!disposed && !frame) frame = requestAnimationFrame(measure);
    }
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    observer?.observe(root);
    observer?.observe(stage.current);
    cards.current.forEach(card => Array.from(card.children).forEach(child => observer?.observe(child)));
    window.addEventListener('resize', schedule, { passive: true });
    root.addEventListener('load', schedule, true);
    document.fonts?.addEventListener?.('loadingdone', schedule);
    document.fonts?.ready.then(schedule);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      root.removeEventListener('load', schedule, true);
      document.fonts?.removeEventListener?.('loadingdone', schedule);
      root.removeAttribute('data-cv-measuring');
    };
  }, [calm]);

  useEffect(() => {
    const root = section.current;
    let frame = 0;
    let settleFrame = 0;
    let settleTimer = 0;
    let disposed = false;
    function commit(timeline, progress, active = Math.round(timeline), arrival = 1) {
      if (disposed) return;
      if (!flow && active !== current.current && cards.current[current.current]?.contains(document.activeElement)) {
        dots.current[active]?.focus({ preventScroll: true });
      }
      current.current = active;
      setPosition(previous => previous.timeline === timeline && previous.progress === progress && previous.active === active && previous.arrival === arrival
        ? previous : { timeline, progress, active, arrival });
    }
    function depthPosition() {
      const range = Math.max(1, root.offsetHeight - stage.current.offsetHeight);
      const progress = clamp(-root.getBoundingClientRect().top / range);
      const units = progress * testimonials.length;
      const index = Math.min(lastIndex, Math.floor(units));
      const rawTimeline = index === lastIndex ? index : index + smooth(.30, .99, units - index);
      const rest = settledPosition.current;
      // Resume from the settled frame instead of snapping back to the old scroll fraction.
      const correction = rest ? rest.offset * (1 - smooth(0, .04, Math.abs(progress - rest.progress))) : 0;
      return { progress, rawTimeline, timeline: clamp(rawTimeline + correction, 0, lastIndex) };
    }
    function update() {
      frame = 0;
      if (disposed || document.hidden) return;
      const rect = root.getBoundingClientRect();
      lastVisible.current = rect.bottom > 0 && rect.top < window.innerHeight;
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
      if (flow) {
        let best = Infinity;
        let active = current.current;
        cards.current.forEach((card, index) => {
          const box = card.getBoundingClientRect();
          if (!calm && !readAll) {
            const entered = smooth(0, 1, (window.innerHeight - box.top) / (window.innerHeight * .65));
            card.style.setProperty('--cv-entry-z', `${-420 * (1 - entered)}px`);
          } else card.style.removeProperty('--cv-entry-z');
          const distance = Math.abs(box.top + Math.min(box.height * .35, 180) - window.innerHeight * .44);
          if (distance < best) { best = distance; active = index; }
        });
        commit(active, active / Math.max(1, lastIndex), active);
      } else {
        const next = depthPosition();
        const arrival = smooth(0, 1, 1 - Math.max(0, rect.top) / (window.innerHeight * .85));
        commit(next.timeline, next.progress, Math.round(next.timeline), arrival);
      }
    }
    function cancelSettle() {
      clearTimeout(settleTimer);
      cancelAnimationFrame(settleFrame);
      settleFrame = 0;
    }
    function settle() {
      if (disposed || flow || document.hidden) return;
      const rect = root.getBoundingClientRect();
      if (rect.top > 0 || rect.bottom < window.innerHeight) return;
      const { timeline: from, rawTimeline, progress } = depthPosition();
      const to = Math.round(from);
      if (Math.abs(from - to) < .001) return;
      const start = performance.now();
      const revision = navigationRevision.current;
      function finish(now) {
        if (disposed || document.hidden || revision !== navigationRevision.current) return;
        const t = clamp((now - start) / 320);
        commit(from + (to - from) * smooth(0, 1, t), progress);
        if (t < 1) settleFrame = requestAnimationFrame(finish);
        else settledPosition.current = { progress, offset: to - rawTimeline };
      }
      settleFrame = requestAnimationFrame(finish);
    }
    function schedule() {
      if (!frame && !disposed) frame = requestAnimationFrame(update);
    }
    function scroll() {
      cancelSettle();
      schedule();
      if (!flow) settleTimer = setTimeout(settle, 350);
    }
    function visibility() {
      cancelSettle();
      cancelAnimationFrame(frame);
      frame = 0;
      if (!document.hidden) schedule();
    }
    schedule();
    window.addEventListener('scroll', scroll, { passive: true });
    window.addEventListener('resize', scroll, { passive: true });
    document.addEventListener('visibilitychange', visibility);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      cancelSettle();
      window.removeEventListener('scroll', scroll);
      window.removeEventListener('resize', scroll);
      document.removeEventListener('visibilitychange', visibility);
    };
  }, [flow, calm, readAll]);

  useLayoutEffect(() => {
    const modeChanged = previousMode.current !== flow;
    const restore = hadDepth.current && modeChanged && lastVisible.current;
    previousMode.current = flow;
    if (!flow) hadDepth.current = true;
    if (pendingAnchor.current === null && !restore) return;
    const focused = cards.current.findIndex(card => card.contains(document.activeElement));
    const index = pendingAnchor.current ?? (focused >= 0 ? focused : current.current);
    pendingAnchor.current = null;
    settledPosition.current = null;
    current.current = index;
    setPosition({ active: index, timeline: index, progress: (index + .30) / testimonials.length, arrival: 1 });
    const root = section.current;
    const top = flow
      ? cards.current[index].getBoundingClientRect().top + window.scrollY - 100
      : root.getBoundingClientRect().top + window.scrollY + (index + .30) / testimonials.length * (root.offsetHeight - stage.current.offsetHeight);
    window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  }, [flow]);

  function jump(index, focusDot = false, instant = false) {
    const active = clamp(index, 0, lastIndex);
    const root = section.current;
    const top = flow
      ? cards.current[active].getBoundingClientRect().top + window.scrollY - 100
      : root.getBoundingClientRect().top + window.scrollY + (active + .30) / testimonials.length * (root.offsetHeight - stage.current.offsetHeight);
    if (flow) cards.current[active]?.focus({ preventScroll: true });
    else if (focusDot) dots.current[active]?.focus({ preventScroll: true });
    current.current = active;
    navigationRevision.current++;
    settledPosition.current = null;
    selectionAnimation.current?.cancel();
    setPosition({ active, timeline: active, progress: (active + .30) / testimonials.length, arrival: 1 });
    setAnnouncement(`${testimonials[active].company}. Testimonial ${active + 1} of ${testimonials.length}.`);
    window.scrollTo({ top: Math.max(0, top), behavior: !flow || instant || calm ? 'instant' : 'smooth' });
    if (!flow && !calm) selectionAnimation.current = cards.current[active].animate?.([
      { transform: 'translate3d(0, 0, -930px)', opacity: .35 },
      { transform: 'translate3d(0, 0, 0px)', opacity: 1 },
    ], { duration: 620, easing: 'cubic-bezier(.16, 1, .3, 1)' });
  }

  function keyboard(event) {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const index = { ArrowLeft: current.current - 1, ArrowRight: current.current + 1, Home: 0, End: lastIndex }[event.key];
    if (index === undefined) return;
    event.preventDefault();
    // Keyboard selection must not be overwritten by intermediate smooth-scroll frames.
    jump(index, true, true);
  }

  return <section id="customer-voices" ref={section} className={`cv-journey ${flow ? 'cv-flow' : 'cv-depth'}${readAll ? ' cv-read-mode' : ''}`} aria-labelledby={`${id}-title`} data-voices-theme="dark" data-cv-layout={flow ? 'reading' : 'depth'} data-cv-motion={calm ? 'reduced' : 'full'}>
    <div className="cv-stage" ref={stage}>
      <div className="cv-heading">
        <div><p className="cv-label">The people behind the partnerships</p><h2 id={`${id}-title`}>Good work. <em>In their words.</em></h2></div>
        {!calm && fits && <button type="button" className="cv-read-all" aria-expanded={readAll} aria-controls={`${id}-cards`} onClick={() => {
          pendingAnchor.current = fits ? current.current : null;
          setReadAll(value => !value);
        }}>{readAll ? 'Back to zoom' : 'Read all'}<FiArrowUpRight aria-hidden="true" /></button>}
      </div>
      <div className="cv-aperture" id={`${id}-cards`} ref={aperture} role="group" aria-label="Customer testimonials">
        {testimonials.map((quote, index) => {
          const logo = voicesMedia.logos.find(item => item.name === quote.company);
          const distance = index - position.timeline;
          const behind = distance > 0;
          const magnitude = Math.abs(distance);
          const front = index === position.active;
          const hidden = !flow && !front;
          // Match the reference's incoming depth, keeping departures inside the reading area.
          const departure = window.innerWidth < 800 ? 80 : 240;
          const z = (behind ? -1860 : departure) * Math.min(.5, magnitude) - 930 * (1 - position.arrival);
          const style = flow ? undefined : {
            transform: `translate3d(0, 0, ${z}px)`,
            opacity: front ? .6 + .4 * (1 - smooth(.06, .5, magnitude)) : 0,
            visibility: front ? 'visible' : 'hidden',
          };
          return <article key={quote.company} ref={node => { cards.current[index] = node; }} className={`cv-card${quote.quote.length > 355 ? ' cv-long' : ''}${front ? ' cv-front' : ''}`} style={style} tabIndex={flow ? -1 : undefined} aria-labelledby={`${id}-client-${index}`} aria-hidden={hidden ? true : undefined} inert={hidden ? '' : undefined}>
            <div className="cv-card-top"><div className="cv-brand" id={`${id}-client-${index}`}>
              {logo ? <img src={logo.src} alt={quote.company} className="cv-logo" loading="lazy" decoding="async" /> : <span className="cv-company">{quote.company}</span>}
            </div></div>
            <div className="cv-reading"><blockquote>{quote.quote}</blockquote></div>
            <div className="cv-card-bottom"><div className="cv-author"><strong>{quote.author}</strong><span>{quote.role}</span><span>{quote.company}</span></div>
              <Link className="cv-story" to={storyPaths[quote.company] || '/work'} tabIndex={hidden ? -1 : undefined}>{storyPaths[quote.company] ? 'See the work' : 'Explore our work'}<FiArrowUpRight aria-hidden="true" /></Link>
            </div>
          </article>;
        })}
      </div>
      <div className="cv-navigation">
        <div className="cv-controls" role="group" aria-label="Testimonial controls" onKeyDown={keyboard}>
          <button type="button" className="cv-arrow" aria-label="Previous customer testimonial" disabled={position.active === 0} onClick={() => jump(current.current - 1, current.current === 1)}><FiArrowLeft aria-hidden="true" /></button>
          <div className="cv-dots" role="group" aria-label="Choose a customer testimonial">{testimonials.map((quote, index) => <button key={quote.company} ref={node => { dots.current[index] = node; }} type="button" className="cv-dot" title={quote.company} aria-label={`${quote.company} testimonial`} aria-pressed={index === position.active} tabIndex={index === position.active ? 0 : -1} onClick={() => jump(index)}><span aria-hidden="true" /></button>)}</div>
          <span className="cv-count" aria-hidden="true">{String(position.active + 1).padStart(2, '0')} / {String(testimonials.length).padStart(2, '0')}</span>
          <button type="button" className="cv-arrow" aria-label="Next customer testimonial" disabled={position.active === lastIndex} onClick={() => jump(current.current + 1, current.current === lastIndex - 1)}><FiArrowRight aria-hidden="true" /></button>
        </div>
        <Link className="cv-continue" to="/contact">What could we build together?<FiArrowUpRight aria-hidden="true" /></Link>
      </div>
      <p className="cv-status" role="status" aria-live="polite" aria-atomic="true">{announcement}</p>
      {!flow && <div className="cv-progress" aria-hidden="true"><span style={{ transform: `scaleX(${position.progress})` }} /></div>}
    </div>
  </section>;
}
